
import { call } from "redux-saga/effects";
import { API_URLS } from "../resources/constants";
import { formQueryString } from '../resources/functions';
import { CallApi } from './callApi';

/**
 * Saga handlers for the 1099 & vendor AP module.
 *
 * Every request carries branch_id in the query string: gl-api resolves the
 * tenant from the JWT plus that branch, so a missing branch_id is a 422 rather
 * than an unscoped read.
 */

export function* getVendorList(action: any): any {
  try {
    const payload = action.payload;
    const url = API_URLS.TAX_1099.VENDOR_LIST + '?' + formQueryString(payload);
    const result = yield call(CallApi.GET, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* getVendorDetails(action: any): any {
  try {
    const { vendor_id, ...params } = action.payload;
    const url = `${API_URLS.TAX_1099.VENDOR_DETAILS}/${vendor_id}?` + formQueryString(params);
    const result = yield call(CallApi.GET, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* createVendor(action: any): any {
  try {
    // branch_id travels in the query string, the vendor fields in the body.
    const { branch_id, ...body } = action.payload;
    const url = `${API_URLS.TAX_1099.VENDOR_CREATE}?` + formQueryString({ branch_id });
    const result = yield call(CallApi.POST, url, body, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* updateVendor(action: any): any {
  try {
    const { vendor_id, branch_id, ...body } = action.payload;
    const url = `${API_URLS.TAX_1099.VENDOR_UPDATE}/${vendor_id}?` + formQueryString({ branch_id });
    const result = yield call(CallApi.PUT, url, body, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* deleteVendor(action: any): any {
  try {
    const { vendor_id, branch_id } = action.payload;
    const url = `${API_URLS.TAX_1099.VENDOR_DELETE}/${vendor_id}?` + formQueryString({ branch_id });
    const result = yield call(CallApi.DELETE, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* getPayerSettings(action: any): any {
  try {
    const url = API_URLS.TAX_1099.PAYER_SETTINGS + '?' + formQueryString(action.payload);
    const result = yield call(CallApi.GET, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* savePayerSettings(action: any): any {
  try {
    const { branch_id, ...body } = action.payload;
    const url = `${API_URLS.TAX_1099.PAYER_SETTINGS}?` + formQueryString({ branch_id });
    const result = yield call(CallApi.PUT, url, body, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* uploadPayerW9(action: any): any {
  try {
    const { branch_id, formData } = action.payload;
    const url = `${API_URLS.TAX_1099.PAYER_W9_UPLOAD}?` + formQueryString({ branch_id });
    const result = yield call(CallApi.POST, url, formData, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* downloadPayerW9Pdf(action: any): any {
  try {
    const { branch_id, file_id } = action.payload;
    // file_id serves a W-9 uploaded but not yet saved. Without it the API
    // reads the saved payer settings, which 404s before the first save and
    // returns the previous document after one.
    const params: Record<string, any> = { branch_id };
    if (file_id) {
      params.file_id = file_id;
    }
    const url = `${API_URLS.TAX_1099.PAYER_W9_PDF}?` + formQueryString(params);
    const result = yield call(CallApi.DOWNLOAD, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* getPaymentList(action: any): any {
  try {
    const url = API_URLS.TAX_1099.PAYMENT_LIST + '?' + formQueryString(action.payload);
    const result = yield call(CallApi.GET, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* getPaymentDetails(action: any): any {
  try {
    const { payment_id, ...params } = action.payload;
    const url = `${API_URLS.TAX_1099.PAYMENT_DETAILS}/${payment_id}?` + formQueryString(params);
    const result = yield call(CallApi.GET, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* createPayment(action: any): any {
  try {
    const { branch_id, ...body } = action.payload;
    const url = `${API_URLS.TAX_1099.PAYMENT_CREATE}?` + formQueryString({ branch_id });
    const result = yield call(CallApi.POST, url, body, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* updatePayment(action: any): any {
  try {
    const { payment_id, branch_id, ...body } = action.payload;
    const url = `${API_URLS.TAX_1099.PAYMENT_UPDATE}/${payment_id}?` + formQueryString({ branch_id });
    const result = yield call(CallApi.PUT, url, body, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* deletePayment(action: any): any {
  try {
    const { payment_id, branch_id } = action.payload;
    const url = `${API_URLS.TAX_1099.PAYMENT_DELETE}/${payment_id}?` + formQueryString({ branch_id });
    const result = yield call(CallApi.DELETE, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* getPaymentSummary(action: any): any {
  try {
    const url = API_URLS.TAX_1099.PAYMENT_SUMMARY + '?' + formQueryString(action.payload);
    const result = yield call(CallApi.GET, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* getFormList(action: any): any {
  try {
    const url = API_URLS.TAX_1099.FORM_LIST + '?' + formQueryString(action.payload);
    const result = yield call(CallApi.GET, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* getFormDetails(action: any): any {
  try {
    const { form_id, ...params } = action.payload;
    const url = `${API_URLS.TAX_1099.FORM_DETAILS}/${form_id}?` + formQueryString(params);
    const result = yield call(CallApi.GET, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* generateFormsBatch(action: any): any {
  try {
    const { branch_id, ...body } = action.payload;
    const url = `${API_URLS.TAX_1099.FORM_GENERATE_BATCH}?` + formQueryString({ branch_id });
    const result = yield call(CallApi.POST, url, body, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* generateFormForVendor(action: any): any {
  try {
    const { vendor_id, branch_id, tax_year, force } = action.payload;
    const url = `${API_URLS.TAX_1099.FORM_GENERATE_VENDOR}/${vendor_id}/forms/generate?`
      + formQueryString({ branch_id, tax_year, force: force || false });
    const result = yield call(CallApi.POST, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* extractW9(action: any): any {
  try {
    // formData carries the file under the multipart field name "file" --
    // CallApi.POST detects FormData and lets axios set the boundary itself.
    const { vendor_id, branch_id, formData } = action.payload;
    const url = `${API_URLS.TAX_1099.VENDOR_LIST}/${vendor_id}/w9/extract?`
      + formQueryString({ branch_id });
    const result = yield call(CallApi.POST, url, formData, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* confirmW9(action: any): any {
  try {
    const { vendor_id, branch_id, ...body } = action.payload;
    const url = `${API_URLS.TAX_1099.VENDOR_LIST}/${vendor_id}/w9/confirm?`
      + formQueryString({ branch_id });
    const result = yield call(CallApi.POST, url, body, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* getCurrentW9(action: any): any {
  try {
    const { vendor_id, branch_id } = action.payload;
    const url = `${API_URLS.TAX_1099.VENDOR_LIST}/${vendor_id}/w9/current?`
      + formQueryString({ branch_id });
    const result = yield call(CallApi.GET, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* downloadW9Pdf(action: any): any {
  try {
    const { vendor_id, branch_id } = action.payload;
    const url = `${API_URLS.TAX_1099.VENDOR_LIST}/${vendor_id}/w9/pdf?`
      + formQueryString({ branch_id });
    const result = yield call(CallApi.DOWNLOAD, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* approveForm(action: any): any {
  try {
    const { form_id, branch_id } = action.payload;
    const url = `${API_URLS.TAX_1099.FORM_APPROVE}/${form_id}/approve?` + formQueryString({ branch_id });
    const result = yield call(CallApi.POST, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* unapproveForm(action: any): any {
  try {
    const { form_id, branch_id } = action.payload;
    const url = `${API_URLS.TAX_1099.FORM_UNAPPROVE}/${form_id}/unapprove?` + formQueryString({ branch_id });
    const result = yield call(CallApi.POST, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* getYearEndPackageStatus(action: any): any {
  try {
    const url = API_URLS.TAX_1099.YEAR_END_STATUS + '?' + formQueryString(action.payload);
    const result = yield call(CallApi.GET, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* approveYearEndPackage(action: any): any {
  try {
    const url = API_URLS.TAX_1099.YEAR_END_APPROVE + '?' + formQueryString(action.payload);
    const result = yield call(CallApi.POST, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* voidForm(action: any): any {
  try {
    const { form_id, branch_id } = action.payload;
    const url = `${API_URLS.TAX_1099.FORM_VOID}/${form_id}/void?` + formQueryString({ branch_id });
    const result = yield call(CallApi.POST, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* furnishFormByEmail(action: any): any {
  try {
    const { form_id, branch_id } = action.payload;
    const url = `${API_URLS.TAX_1099.FORM_FURNISH_EMAIL}/${form_id}/furnish/email?`
      + formQueryString({ branch_id });
    const result = yield call(CallApi.POST, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* correctForm(action: any): any {
  try {
    const { form_id, branch_id, correction_type } = action.payload;
    const url = `${API_URLS.TAX_1099.FORM_CORRECT}/${form_id}/correct?` + formQueryString({ branch_id });
    const result = yield call(CallApi.POST, url, { correction_type }, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* downloadFormPdf(action: any): any {
  try {
    const { form_id, branch_id } = action.payload;
    const url = `${API_URLS.TAX_1099.FORM_PDF}/${form_id}/pdf?` + formQueryString({ branch_id });
    // CallApi.DOWNLOAD sets responseType 'blob' and the Authorization header --
    // a plain window.open would hit gl-api unauthenticated and get a 401.
    const result = yield call(CallApi.DOWNLOAD, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* downloadStatementEvidence(action: any): any {
  try {
    const { file_id, branch_id } = action.payload;
    const url = `${API_URLS.TAX_1099.STATEMENT_EVIDENCE}/${file_id}?` + formQueryString({ branch_id });
    // Same blob path as downloadFormPdf -- gl-api needs the Authorization
    // header, so window.open on the raw URL would 401.
    const result = yield call(CallApi.DOWNLOAD, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* syncQboVendors(action: any): any {
  try {
    const { branch_id } = action.payload;
    const url = `${API_URLS.TAX_1099.VENDOR_SYNC_QBO}?` + formQueryString({ branch_id });
    const result = yield call(CallApi.POST, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* syncExpenses(action: any): any {
  try {
    const { branch_id, tax_year } = action.payload;
    const params: Record<string, any> = { branch_id };
    if (tax_year) {
      params.tax_year = tax_year;
    }
    const url = `${API_URLS.TAX_1099.PAYMENT_SYNC_EXPENSES}?` + formQueryString(params);
    const result = yield call(CallApi.POST, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* getVendorPayments(action: any): any {
  try {
    const { vendor_id, ...params } = action.payload;
    const url = `${API_URLS.TAX_1099.VENDOR_PAYMENTS}/${vendor_id}/payments?` + formQueryString(params);
    const result = yield call(CallApi.GET, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* getNextVendorCode(action: any): any {
  try {
    const payload = action.payload;
    const url = API_URLS.TAX_1099.VENDOR_NEXT_CODE + '?' + formQueryString(payload);
    const result = yield call(CallApi.GET, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}



export function* getUnmappedPayees(action: any): any {
  try {
    const url = API_URLS.TAX_1099.UNMAPPED_PAYEES + '?' + formQueryString(action.payload);
    const result = yield call(CallApi.GET, url, {}, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}

export function* resolvePayee(action: any): any {
  try {
    const { branch_id, ...body } = action.payload;
    const url = `${API_URLS.TAX_1099.RESOLVE_PAYEE}?branch_id=${branch_id}`;
    const result = yield call(CallApi.POST, url, body, true);
    action && action.callbackSuccess && action.callbackSuccess(result);
  } catch (e) {
    action && action.callbackError && action.callbackError(e);
  }
}
